import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { BillingMetricsRecord, CreditLot, LedgerEntry, Wallet, WalletConfig, WalletType } from '../../models/Wallet'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _BASE_URL = import.meta.env.REACT_APP_ACCOUNTS_API_BASE_URL!

export interface GetBillingMetricsRecordRequest extends BaseRequest {
  billingMetricsRecordId: string
}

export interface GetWalletRequest extends BaseRequest {
  walletType: WalletType
}

export interface GetLedgerRequest extends BasePageRequest {
  walletType: WalletType
}

export interface GetLotsRequest extends BaseRequest {
  walletType: WalletType
}

export interface UpdateWalletConfigRequest extends BaseRequest {
  walletType: WalletType
  payload: {
    config: WalletConfig
  }
}

export const walletApi = createApi({
  reducerPath: 'walletApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Wallet', 'Ledger', 'Lots'],
  endpoints: (builder) => ({
    getWallet: builder.query<BaseResponse<Wallet>, GetWalletRequest>({
      query: (request) => `/coins/wallets/${request.accountId}/${request.walletType}`,
      providesTags: ['Wallet'],
    }),
    updateWalletConfig: builder.mutation<BaseResponse<Wallet>, UpdateWalletConfigRequest>({
      query: (request) => ({
        url: `/coins/wallets/${request.accountId}/${request.walletType}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['Wallet'],
    }),
    getLedger: builder.query<BasePageResponse<LedgerEntry[]>, GetLedgerRequest>({
      query: (request) => ({
        url: `/coins/wallets/${request.accountId}/${request.walletType}/ledger`,
        params: {
          page: request.pageRequest.page,
          size: request.pageRequest.size,
        },
      }),
      providesTags: ['Ledger'],
    }),
    getLots: builder.query<BaseResponse<CreditLot[]>, GetLotsRequest>({
      query: (request) => `/coins/wallets/${request.accountId}/${request.walletType}/lots`,
      providesTags: ['Lots'],
    }),
    getBillingMetricsRecord: builder.query<BaseResponse<BillingMetricsRecord>, GetBillingMetricsRecordRequest>({
      query: (request) => `/accounts/${request.accountId}/billing-metrics/records/${request.billingMetricsRecordId}`,
    }),
  }),
})

export const { useGetWalletQuery, useGetLedgerQuery, useGetLotsQuery, useLazyGetBillingMetricsRecordQuery, useUpdateWalletConfigMutation } = walletApi
